import React, { useEffect } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText
} from '@mui/material';

interface GPCIDropdownProps {
    selectedState: string | null;
    gpciLocalityData: Array<{state: string, gpci: string, locality: string}>;
    value: string | null;
    onChange: (value: string) => void;
    onBlur?: () => void;
    error?: string;
    disabled?: boolean;
    required?: boolean;
}

const GPCIDropdown: React.FC<GPCIDropdownProps> = ({
    selectedState,
    gpciLocalityData,
    value,
    onChange,
    onBlur,
    error,
    disabled = false,
    required = false
}) => {
    // Filter GPCI values from gpciLocalityData based on selected state
    const gpciValues = React.useMemo(() => {
        if (!selectedState || !gpciLocalityData.length) {
            return [];
        }

        // Filter by state and get unique GPCI values
        const filtered = gpciLocalityData
            .filter(item => item.state.toUpperCase() === selectedState.toUpperCase())
            .map(item => item.gpci);

        // Remove duplicates and sort
        const unique = Array.from(new Set(filtered)).sort();
        return unique;
    }, [selectedState, gpciLocalityData]);

    // Clear selection if GPCI values change and current value is not in the list
    useEffect(() => {
        if (value && gpciValues.length > 0 && !gpciValues.includes(value)) {
            onChange('');
        }
    }, [gpciValues, value, onChange]);

    const isDisabled = disabled || !selectedState;

    return (
        <FormControl fullWidth error={!!error} disabled={isDisabled} required={required}>
            <InputLabel id="gpci-select-label">GPCI</InputLabel>
            <Select
                labelId="gpci-select-label"
                id="gpci-select"
                value={value || ''}
                label="GPCI"
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
            >
                <MenuItem value="">
                    <em>{selectedState ? 'Select GPCI' : 'Select State First'}</em>
                </MenuItem>
                {gpciValues.map((gpci) => (
                    <MenuItem key={gpci} value={gpci}>
                        {gpci}
                    </MenuItem>
                ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    );
};

export default GPCIDropdown;
